import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useSearchCardsQuery } from 'store/services/cards/search/cardsSearchApi';
import { useGetUserHoverCardQuery } from 'store/services/profile/profileApi';
import { useUpdateBannerMutation } from 'store/services/user/meApi';
import UserHoverCard from 'components/UserHoverCard/UserHoverCard';
import { MTGCard } from 'types/interfaces/mtgCard';
import './BannerPickerModal.css';

/**
 * Elite hover card banner picker (Asana 1219946182379030, Decision 7): search
 * English printings newest first, pick a tile, preview it on the member's real
 * hover card rendered inline, save through PUT /users/banner.
 */
export const BANNER_SEARCH_DEBOUNCE_MS = 250;
export const BANNER_PICKER_PAGE_SIZE = 24;
/** Shortest query the search runs for. */
const BANNER_SEARCH_MIN_CHARS = 2;

interface BannerPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The member's own username: the preview fetches their real hover card. */
  username: string;
  /** The banner saved today, so the matching tile preselects and Save stays off until a different one is picked. */
  currentBannerUrl: string | null;
}

interface Selection {
  scryfallId: string;
  artUrl: string;
}

const noop = () => {};

const cardScryfallId = (card: MTGCard): string => card.scryfallID ?? card.scryfallId ?? '';

const BannerPickerModal: React.FC<BannerPickerModalProps> = ({ isOpen, onClose, username, currentBannerUrl }) => {
  const [query, setQuery] = useState('');
  const [selection, setSelection] = useState<Selection | null>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), BANNER_SEARCH_DEBOUNCE_MS);

  // Reset on open so a reopened picker starts from the saved banner.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelection(null);
    }
  }, [isOpen]);

  const { data: results, isFetching } = useSearchCardsQuery(
    {
      name: debouncedQuery,
      view: 'printings',
      lang: 'en',
      // The search allowlist has release_date, not released (sortOptions.ts).
      sort: 'release_date',
      direction: 'desc',
      pageSize: BANNER_PICKER_PAGE_SIZE,
    },
    { skip: !isOpen || debouncedQuery.length < BANNER_SEARCH_MIN_CHARS },
  );

  const { data: hoverData } = useGetUserHoverCardQuery(username, { skip: !isOpen || !username });
  const [updateBanner, { isLoading: isSaving }] = useUpdateBannerMutation();

  const selectedArtUrl = selection?.artUrl ?? currentBannerUrl;
  const canSave = !!selection && selection.artUrl !== currentBannerUrl && !isSaving;

  const tiles = useMemo(
    () =>
      (results?.cards ?? []).map((card) => {
        const scryfallId = cardScryfallId(card);
        return {
          key: `${scryfallId}-${card.id}`,
          scryfallId,
          artUrl: AssetUrls.cards.getCardImage(scryfallId, 'art_crop'),
          name: card.name,
          setCode: (card.setCode ?? '').toUpperCase(),
        };
      }),
    [results],
  );

  const handleSave = async () => {
    if (!selection) return;
    try {
      await updateBanner({ scryfallId: selection.scryfallId }).unwrap();
      onClose();
    } catch {
      toast.error('Failed to save banner');
    }
  };

  if (!isOpen) return null;

  const showEmptyState = debouncedQuery.length < BANNER_SEARCH_MIN_CHARS && !currentBannerUrl;
  const previewData = hoverData ? { ...hoverData, bannerUrl: selectedArtUrl } : undefined;

  return createPortal(
    <div className="banner-picker-overlay" onClick={onClose}>
      <div
        className="banner-picker-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="banner-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="banner-picker-header">
          <h3 id="banner-picker-title">Choose banner art</h3>
          <button type="button" className="banner-picker-close" onClick={onClose} aria-label="Close">
            <img src={AssetUrls.icons.dropdowns.close} alt="" />
          </button>
        </div>

        <div className="banner-picker-body">
          <div className="banner-picker-search-column">
            <input
              type="text"
              className="banner-picker-search"
              placeholder="Search card name"
              aria-label="Search card name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />

            {showEmptyState ? (
              <p className="banner-picker-empty">Search for any card to use its art.</p>
            ) : (
              <div className="banner-picker-grid" data-testid="banner-picker-grid">
                {tiles.map((tile) => {
                  const selected = selectedArtUrl === tile.artUrl;
                  return (
                    <button
                      type="button"
                      key={tile.key}
                      className={`banner-picker-tile${selected ? ' banner-picker-tile--selected' : ''}`}
                      onClick={() => setSelection({ scryfallId: tile.scryfallId, artUrl: tile.artUrl })}
                      aria-pressed={selected}
                    >
                      <span className="banner-picker-tile-art">
                        <img src={tile.artUrl} alt="" loading="lazy" />
                        {selected && (
                          <FontAwesomeIcon icon={faCircleCheck} className="banner-picker-tile-check" aria-hidden="true" />
                        )}
                      </span>
                      <span className="banner-picker-tile-caption">
                        {tile.name} · {tile.setCode}
                      </span>
                    </button>
                  );
                })}
                {isFetching && tiles.length === 0 && <p className="banner-picker-status">Searching…</p>}
                {!isFetching && debouncedQuery.length >= BANNER_SEARCH_MIN_CHARS && tiles.length === 0 && (
                  <p className="banner-picker-status">No cards found</p>
                )}
              </div>
            )}

            <p className="banner-picker-note">Every English printing is listed, newest first.</p>
          </div>

          <div className="banner-picker-preview-column">
            <span className="banner-picker-preview-label">PREVIEW</span>
            <div className="banner-picker-preview">
              <UserHoverCard
                inline
                data={previewData}
                isLoading={!previewData}
                position={{ left: 0, top: 0 }}
                cardProps={{ onMouseEnter: noop, onMouseLeave: noop }}
              />
            </div>
          </div>
        </div>

        <div className="banner-picker-footer">
          <button type="button" className="settings-customize-border-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="settings-customize-border-btn settings-customize-border-btn--primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSaving ? 'Saving...' : 'Save banner'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BannerPickerModal;
